import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { peep_client } from '~/src/api/peep';
import { useAbort } from '~src/utils/useAbort';
import { Button, TextInput, Text, Paper, Stack, Title, Alert } from '@mantine/core';
import { AlertCircle } from 'lucide-react';

type ForgotPasswordState =
  | { status: 'idle'; email: string }
  | { status: 'submitting'; email: string }
  | { status: 'error'; email: string; error: string };

export default function ForgotPassword() {
  const [state, setState] = useState<ForgotPasswordState>({ status: 'idle', email: '' });
  const navigate = useNavigate();
  const abortRef = useAbort();

  const updateEmail = (email: string) => {
    setState((s) => (s.status === 'submitting' ? s : { status: 'idle', email }));
  };

  const submit = async () => {
    if (state.status === 'submitting') return;

    const { email } = state;
    setState({ status: 'submitting', email });

    try {
      await peep_client.users.requestPasswordReset(email, { signal: abortRef.current.signal });
      navigate('/reset-password');
    } catch (e) {
      console.error(e);
      setState({ status: 'error', email, error: 'An error occurred. Please try again.' });
    }
  };

  const isLoading = state.status === 'submitting';
  const error = state.status === 'error' ? state.error : null;
  const canSubmit = state.email && (state.status === 'idle' || state.status === 'error');

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        minHeight: '100dvh',
        backgroundColor: 'var(--global-gray-lighter-7)',
        padding: '2rem',
      }}
    >
      <Paper
        style={{
          width: 'min(400px, 90vw)',
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '2rem',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          border: '1px solid #f0f0f0',
        }}
      >
        <Stack gap="lg">
          <div style={{ textAlign: 'center' }}>
            <Title order={2} style={{ fontFamily: 'Lato', color: '#333', marginBottom: '0.5rem' }}>
              Reset Your Password
            </Title>
            <Text size="sm" style={{ fontFamily: 'Lato', color: '#666' }}>
              Enter your email and we'll send you a reset code
            </Text>
          </div>

          {error && (
            <Alert icon={<AlertCircle size={16} />} color="red" style={{ fontFamily: 'Lato' }}>
              {error}
            </Alert>
          )}

          <TextInput
            placeholder="Enter your email"
            type="email"
            value={state.email}
            onChange={(e) => updateEmail(e.target.value)}
            disabled={isLoading}
            size="md"
            styles={{
              input: {
                fontFamily: 'Lato',
                borderRadius: '8px',
                border: '1px solid #e0e0e0',
              },
            }}
          />

          <Stack gap="sm">
            <Button
              onClick={submit}
              disabled={!canSubmit}
              loading={isLoading}
              size="md"
              style={{
                backgroundColor: 'var(--global-main)',
                fontFamily: 'Lato',
                fontWeight: '600',
                borderRadius: '8px',
              }}
              fullWidth
            >
              Send Reset Code
            </Button>

            <Button
              onClick={() => navigate('/signin')}
              variant="subtle"
              size="md"
              style={{
                fontFamily: 'Lato',
                color: 'var(--global-main)',
              }}
              fullWidth
            >
              Back to Sign In
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </div>
  );
}
